import { useState } from 'react'
import { Download } from 'lucide-react'
import type { AxiosError } from 'axios'
import { http } from '../../store/axiosBaseQuery.js'
import { Button } from '../ui/Button.js'
import { toast } from '../feedback/Toaster.js'

export interface ExportButtonProps {
  /** `/v1/admin/exports/bookings`, and the filters the table itself is using. */
  url: string
  params?: Record<string, string | number | undefined>
  filename: string
  label?: string
}

/**
 * Take a copy of what is on screen.
 *
 * *"One route for all six tables, producing a spreadsheet file with the same
 * filters the table itself takes, and recording who took a copy."* The filters
 * matter: an export that quietly returns everything when the table is showing one
 * city is a file somebody will act on.
 *
 * Downloads rather than opens. A spreadsheet rendered in a browser tab is a
 * spreadsheet nobody can save.
 *
 * **It says what went wrong.** It used to answer *"That export could not be
 * taken. Try again."* to everything, and every export on the desk was
 * answering 401 — the token was attached inside `axiosBaseQuery` and this
 * reaches for `http` directly. Trying again would never have worked, and the
 * message said to. The server's own sentence now comes through, which is how
 * the next one of these gets found in a minute rather than a month.
 */
/** The server's own sentence, dug out of a response that was asked for as a blob. */
async function reasonFor(err: unknown): Promise<string> {
  const e = err as AxiosError<Blob>
  const status = e.response?.status
  if (status === undefined) return 'No connection. Check your network and try again.'
  try {
    const body = e.response?.data
    const text = body instanceof Blob ? await body.text() : JSON.stringify(body)
    const message = (JSON.parse(text) as { error?: { message?: string } }).error?.message
    if (message) return message
  } catch {
    // Not JSON, or not readable. The status line below is still better than
    // "try again" on something that will never succeed by trying again.
  }
  if (status === 401) return 'You have been signed out. Sign in again and take the copy.'
  if (status === 403) return 'Your role cannot take a copy of this.'
  return `That export could not be taken (${status}).`
}

export function ExportButton({ url, params, filename, label = 'Export' }: ExportButtonProps) {
  const [busy, setBusy] = useState(false)
  return (
    <Button
      variant="secondary"
      /* Icon only on a phone: this sits beside a search box in a page
         heading, and three controls and a search box do not fit 390px. */
      compact
      /* The same height as everything it stands next to. It was `sm` — 32px
         against the 40px search box and 40px primary button beside it, which
         reads as a mistake rather than as a smaller kind of action. */
      loading={busy}
      leadingIcon={<Download />}
      onClick={async () => {
        setBusy(true)
        try {
          const res = await http.get<Blob>(url, { params, responseType: 'blob' })
          const href = URL.createObjectURL(res.data)
          const a = document.createElement('a')
          a.href = href
          a.download = filename
          a.click()
          URL.revokeObjectURL(href)
        } catch (err) {
          /* The body is a blob, because that is what was asked for — so the
             error's sentence has to be read out of it rather than plucked
             off `response.data.error`. */
          toast.error(await reasonFor(err))
        } finally {
          setBusy(false)
        }
      }}
    >
      {label}
    </Button>
  )
}
